import React from "react";
import { CategoriesBlockProps } from "../../types/searchResults";
import { CategoryResult } from "../../search-core/types/CategoryResult";

/**
 * CategoriesBlock - Displays product categories from search results
 *
 * Shows real categories extracted from feed (dev) or Bitrix (prod).
 * Uses normalized CategoryResult interface for consistent UI consumption.
 */
const CategoriesBlock: React.FC<CategoriesBlockProps> = ({
  categories = [],
  className = "",
  onCategorySelect,
}) => {
  const handleCategoryClick = (category: CategoryResult) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    } else if (category.url) {
      // Navigate to category URL
      window.location.href = category.url;
    } else {
      console.log("Category selected:", category.title, category.id);
    }
  };

  // Don't render block if there are no categories
  if (categories.length === 0) {
    return null;
  }

  return (
    <div className={`categories-block ${className}`}>
      <ul style={styles.list}>
        {categories
          .filter((_, i) => i < 3)
          .map((category) => (
            <li key={category.id} style={styles.item}>
              <button
                onClick={() => handleCategoryClick(category)}
                className="category-button"
                aria-label={`Filter by ${category.title}`}
              >
                <span className="flex gap-3 items-center">
                  {category.image ? (
                    <img src={category.image} alt="Category icon" style={styles.categoryIcon} />
                  ) : (
                    <i className="fas fa-folder" style={styles.categoryIcon} />
                  )}
                  <div className="flex flex-col gap-1">
                    {category.title}
                    <span className="font-normal text-[12px] leading-[12px] tracking-normal text-[#909499]">
                      Категория
                    </span>
                  </div>
                </span>
                {category.level && category.level > 1 && (
                  <span style={styles.levelIndicator} title={`Level ${category.level}`}>
                    {category.level}
                  </span>
                )}
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
};

// Minimal inline styles
const styles = {
  block: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  },
  title: {
    margin: "0 0 12px 0",
    fontSize: "16px",
    fontWeight: 600,
    color: "#334155",
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  item: {
    marginBottom: "8px",
  },
  categoryButton: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 12px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#64748b",
    textAlign: "left" as const,
    transition: "background-color 0.2s ease",
  },
  categoryIcon: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "40px",
    height: "40px",
  },
  categoryText: {
    flex: 1,
  },
  levelIndicator: {
    fontSize: "14px",
    color: "#5D5F61",
    backgroundColor: "#2222220A",
    borderRadius: "8px",
    padding: "4px 8px",
  },
};

export default CategoriesBlock;
